import _isUndefined from "lodash/isUndefined";
import _omitBy from "lodash/omitBy";
import React, { useEffect, useRef, useState } from "react";
import ProductAreaRail from "web/react/components/product-area-rail/product-area-rail";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import userProfiler from "web/script/modules/userprofiler";
import { RecentlyViewedSerializer } from "web/types/serializers";

const RECENTLY_VIEWED_ENDPOINT = "modules/recently_viewed";

function getRecentlySeenProductIds(number: number, currentProductId: number | null): number[] {
    // If we're on a product page we're going to exclude
    // the current product from the recently viewed module.
    if (currentProductId) {
        number++;
    }

    const seenProducts = userProfiler.getSeenProducts().slice(-number).reverse();
    const productIds = seenProducts.reduce((memo, product) => {
        if (product.productId !== currentProductId) {
            memo.push(product.productId);
        }

        return memo;
    }, []);

    return productIds;
}

function makeApiParams(recentlyViewedProducts, inOverlay, originFeed): any {
    return _omitBy(
        {
            product_id: recentlyViewedProducts,
            in_overlay: inOverlay,
            origin_feed: originFeed,
        },
        _isUndefined
    );
}

function RecentlyViewed({
    title,
    current_product_id: currentProductId,
    in_overlay: inOverlay,
    origin_feed: originFeed,
}: RecentlyViewedSerializer): React.ReactElement | null {
    const [recentlySeenProductIds, setRecentlySeenProductIds] = useState<number[] | null>(null);
    const params = makeApiParams(recentlySeenProductIds, inOverlay, originFeed);
    const analyticsEventLabel = "recently-viewed-product";

    const { data, isFetching, run } = useRothko<RecentlyViewedSerializer>(
        RECENTLY_VIEWED_ENDPOINT,
        params,
        {
            enabled: false,
        }
    );

    const recentlyViewedRef = useRef<HTMLDivElement | null>(null);
    const [isVisible] = useIntersectionObserver(
        {
            rootMargin: "0px 0px 250px 0px",
            once: true,
        },
        recentlyViewedRef as any
    );

    useEffect(() => {
        if (isVisible) {
            let recentlySeen = getRecentlySeenProductIds(10, currentProductId);
            setRecentlySeenProductIds(recentlySeen);

            if (recentlySeen && recentlySeen.length) {
                run();
            }
        }
    }, [isVisible, currentProductId, run, data]);

    // Don't render if we didn't store recently seen products
    if (recentlySeenProductIds !== null && !recentlySeenProductIds.length) {
        return null;
    }

    return (
        <div ref={recentlyViewedRef}>
            <ProductAreaRail
                analyticsEventLabel={analyticsEventLabel}
                title={title}
                isLoading={isFetching}
                products={data?.products}
            />
        </div>
    );
}

export default RecentlyViewed;
